import React from 'react'

export interface WorkspaceProfileItem {
  id: string
  label: string
  icon: React.ReactNode
}

export interface WorkspaceModuleItem {
  id: string
  label: string
  icon: React.ReactNode
  badge?: number
}

interface FixedWorkspaceNavProps {
  profiles: WorkspaceProfileItem[]
  activeProfile: string
  onProfileChange: (profileId: string) => void
  modules: WorkspaceModuleItem[]
  activeModule: string | null
  onModuleChange: (moduleId: string) => void
  darkMode?: boolean
  ariaLabel: string
  trailing?: React.ReactNode
}

/**
 * Two-level Fixed-workspace navigation: a profile row (Tracking /
 * Operations / Administration) and, when the active profile exposes
 * multiple modules, a second row of module tabs so labels never truncate.
 */
const FixedWorkspaceNav: React.FC<FixedWorkspaceNavProps> = ({
  profiles,
  activeProfile,
  onProfileChange,
  modules,
  activeModule,
  onModuleChange,
  darkMode = false,
  ariaLabel,
  trailing
}) => {
  return (
    <nav
      className={`flex-shrink-0 border-b ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}
      aria-label={ariaLabel}
    >
      <div className="flex min-w-0 items-center gap-1 overflow-x-auto px-3 pt-2 pb-1">
        {profiles.map(profile => (
          <button
            key={profile.id}
            onClick={() => onProfileChange(profile.id)}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-bold whitespace-nowrap transition-colors ${
              activeProfile === profile.id
                ? 'bg-blue-600 text-white shadow-sm'
                : darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-current={activeProfile === profile.id ? 'page' : undefined}
          >
            {profile.icon}{profile.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">{trailing}</div>
      </div>
      {modules.length > 0 && (
        <div className="flex min-w-0 items-center gap-1 overflow-x-auto px-3 pb-2">
          {modules.map(module => (
            <button
              key={module.id}
              onClick={() => onModuleChange(module.id)}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeModule === module.id
                  ? darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-700'
                  : darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'
              }`}
              aria-current={activeModule === module.id ? 'page' : undefined}
            >
              {module.icon}{module.label}
              {typeof module.badge === 'number' && module.badge > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] text-white">
                  {module.badge > 99 ? '99+' : module.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </nav>
  )
}

export default FixedWorkspaceNav
